/**
 * Landing page for a GitHub login.
 *
 * GitHub redirects to the Worker, which never hands the browser a session token:
 * it issues a one-time ticket and sends it here, and this page exchanges it over
 * POST. Failures arrive as a reason code instead of an error body, because the
 * visitor is in the middle of a navigation and needs a way forward.
 */

import { useEffect, useRef } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'

import { sanitizeRedirectPath } from '@shared/redirect'
import { GITHUB_OAUTH_ERROR_REASONS, type GithubOAuthErrorReason } from '@shared/types'

import { PageHeader } from '../../components/layout/page-header'
import { LoadingBlock } from '../../components/layout/states'
import { Alert, AlertDescription } from '../../components/ui/alert'
import { Button } from '../../components/ui/button'
import { Card, CardContent } from '../../components/ui/card'
import { useExchangeGithubTicket } from '../../hooks/use-auth'
import { mutationErrorText } from '../../lib/mutation-error'
import { withReferralParams } from '../../lib/referral-capture'

function isReason(value: string | null): value is GithubOAuthErrorReason {
  return value !== null && (GITHUB_OAUTH_ERROR_REASONS as readonly string[]).includes(value)
}

export function LoginGithubPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const exchange = useExchangeGithubTicket()

  const ticket = searchParams.get('ticket')
  const errorParam = searchParams.get('error')
  const redirect = sanitizeRedirectPath(searchParams.get('redirect'))

  // The exchange must run once: a re-render (or React's double invocation in
  // development) would spend the single-use ticket twice.
  const started = useRef(false)

  useEffect(() => {
    if (!ticket || started.current) {
      return
    }
    started.current = true
    exchange.mutate(ticket, {
      onSuccess: () => {
        navigate(redirect, { replace: true })
      },
    })
  }, [exchange, navigate, redirect, ticket])

  // Read as text rather than as an `ApiError`: the exchange failing for any
  // reason, including a request that never produced a response, has to end the
  // waiting state with the failure notice.
  const exchangeError = mutationErrorText(exchange.error, t)
  const reason: GithubOAuthErrorReason | null = isReason(errorParam)
    ? errorParam
    : ticket
      ? exchangeError
        ? 'exchange_failed'
        : null
      : 'state_invalid'

  // Retrying re-enters the flow, so the captured referral code travels again.
  const retryHref = withReferralParams('/api/auth/github/start', { redirect })

  return (
    <div className="container-narrow flex flex-col gap-6">
      <PageHeader
        breadcrumbs={[
          { label: t('nav.home'), to: '/' },
          { label: t('auth.loginTitle'), to: '/login/email' },
          { label: t('auth.githubTitle') },
        ]}
        title={t('auth.githubTitle')}
      />

      <Card>
        <CardContent className="flex flex-col gap-4 pt-4 sm:pt-5">
          {ticket && !reason ? (
            <LoadingBlock label={t('auth.githubPending')} />
          ) : (
            <>
              <Alert variant={reason === 'access_denied' ? 'info' : 'destructive'}>
                <AlertDescription>
                  {t(`auth.githubError.${reason ?? 'provider_error'}`)}
                </AlertDescription>
              </Alert>
              <div className="flex flex-wrap gap-2">
                <Button asChild>
                  <a href={retryHref}>{t('auth.githubRetry')}</a>
                </Button>
                <Button variant="outline" asChild>
                  <Link to="/login/email">{t('auth.useEmail')}</Link>
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
